/**
 * Pure step model for the customer onboarding shell (CLA-449).
 *
 * The left-rail stepper, the Connect sub-step breadcrumb, and the resume
 * redirect are all driven by these pure functions so they can be unit-tested
 * without rendering or atoms. Nothing here touches the DOM, Clerk, or the RPC
 * client — callers map `onboarding/state` (CLA-428) into these primitives.
 *
 * No "wizard" anywhere: the surface is an onboarding *shell* with a *step rail*.
 *
 * @since 1.0.0
 */
import type { OnboardingStep } from "@fm/api/rpc";
import { Match } from "effect";

/**
 * The six rail stops, in order. `account` is the pre-state stop (sign-up /
 * link bind), `done` is terminal; `connect` and the stops before it mirror the
 * server's derived `OnboardingStep`, while `verify` is the client-driven live
 * test that runs after Connect and before the terminal `done` hand-off.
 *
 * @since 1.0.0
 */
export type OnboardingRailStepId = "account" | "billing" | "install" | "connect" | "verify" | "done";

/**
 * Ordered rail definition — id + human label. Order is significant: it drives
 * both the rendered top-to-bottom rail and the completed/current/upcoming
 * status comparison.
 *
 * @since 1.0.0
 */
export const ONBOARDING_RAIL_STEPS: ReadonlyArray<{ readonly id: OnboardingRailStepId; readonly label: string }> = [
  { id: "account", label: "Account" },
  { id: "billing", label: "Billing" },
  { id: "install", label: "Install" },
  { id: "connect", label: "Connect" },
  { id: "verify", label: "Verify" },
  { id: "done", label: "Done" },
];

/**
 * Where the terminal Done hand-off and the Verify "skip to dashboard" escape
 * hatch both send the customer: their (future) customer dashboard. The
 * dashboard itself is a separate project — this is the agreed forward-looking
 * path so the onboarding flow has a real, single hand-off target now.
 *
 * @since 1.0.0
 */
export const CUSTOMER_DASHBOARD_PATH = "/clawinstall/dashboard" as const;

/**
 * The Connect stop's sub-steps, tracked by the top sub-stepper. The redesigned
 * flow (CLA-850) routes Codex → a `services` hub → Discord → the optional Google
 * tools → the final Review & activate step; the OAuth-callback return set mirrors
 * these ids so a provider round-trip resumes on the right sub-step.
 *
 * @since 1.0.0
 */
export type OnboardingConnectSubStepId = "codex" | "services" | "discord" | "google" | "review";

/**
 * Ordered Connect sub-step definition (Codex → services hub → Discord →
 * optional Google tools → Review & activate).
 *
 * @since 1.0.0
 */
export const ONBOARDING_CONNECT_SUBSTEPS = [
  { id: "codex", label: "Connect Codex" },
  { id: "services", label: "Connect services" },
  { id: "discord", label: "Set up Discord" },
  { id: "google", label: "Optional tools" },
  { id: "review", label: "Review & activate" },
] as const;

/**
 * Type guard for {@link OnboardingConnectSubStepId} — validates the Connect
 * stop's `?step=` search param (the sub-step lives in the URL so it survives
 * OAuth round-trips, reloads, and resume links).
 *
 * @since 1.0.0
 */
export const isConnectSubStepId = (value: unknown): value is OnboardingConnectSubStepId =>
  value === "codex" || value === "services" || value === "discord" || value === "google" || value === "review";

/**
 * The three onboarding phases the redesigned top stepper renders
 * (`01 Welcome — 02 Connect — 03 Ready`). Each rail stop folds into one phase.
 *
 * @since 1.0.0
 */
export type OnboardingPhaseId = "welcome" | "connect" | "ready";

/**
 * Ordered phase definition — id + label. Order is significant: it drives the
 * rendered left-to-right stepper and the completed/current/upcoming comparison.
 *
 * @since 1.0.0
 */
export const ONBOARDING_PHASES: ReadonlyArray<{ readonly id: OnboardingPhaseId; readonly label: string }> = [
  { id: "welcome", label: "Welcome" },
  { id: "connect", label: "Connect" },
  { id: "ready", label: "Ready" },
];

/**
 * Fold a rail stop into its phase: the account/billing/install stops are the
 * Welcome phase, `connect` is its own phase, and the post-Connect verify/done
 * stops are the Ready phase.
 *
 * @since 1.0.0
 */
export const phaseForRailStep = (id: OnboardingRailStepId): OnboardingPhaseId =>
  Match.value(id).pipe(
    Match.when("account", () => "welcome" as const),
    Match.when("billing", () => "welcome" as const),
    Match.when("install", () => "welcome" as const),
    Match.when("connect", () => "connect" as const),
    Match.when("verify", () => "ready" as const),
    Match.when("done", () => "ready" as const),
    Match.exhaustive,
  );

/**
 * Completed/current/upcoming for a phase, by position relative to `current`.
 *
 * @since 1.0.0
 */
export const phaseStatus = (
  current: OnboardingPhaseId,
  phase: OnboardingPhaseId,
): OnboardingStepStatus => {
  const order = ONBOARDING_PHASES.map((p) => p.id);
  const currentIndex = order.indexOf(current);
  const phaseIndex = order.indexOf(phase);
  if (phaseIndex < currentIndex) return "completed";
  if (phaseIndex === currentIndex) return "current";
  return "upcoming";
};

/**
 * The status of a single stop relative to the current one.
 *
 * @since 1.0.0
 */
export type OnboardingStepStatus = "completed" | "current" | "upcoming";

/**
 * Base path for every onboarding route. Lives under `/clawinstall` so the flow
 * inherits the ClawInstall theme + domain rewrite (`clawinstall.ai`).
 *
 * @since 1.0.0
 */
export const ONBOARDING_BASE_PATH = "/clawinstall/onboarding";

/**
 * Route path for a rail stop, e.g. `/clawinstall/onboarding/billing`.
 *
 * @since 1.0.0
 */
export const onboardingStepPath = (id: OnboardingRailStepId): string => `${ONBOARDING_BASE_PATH}/${id}`;

/**
 * Map the server's derived `OnboardingStep` onto a rail stop. The server has
 * already folded intake/agent/connections into `step`; `create_agent` and
 * `install` both land on the Install stop (materialise then phone-home are two
 * phases of the same rail stop).
 *
 * @since 1.0.0
 */
export const railStepForBackendStep = (step: OnboardingStep): OnboardingRailStepId =>
  Match.value(step).pipe(
    Match.when("checkout", () => "billing" as const),
    Match.when("create_agent", () => "install" as const),
    Match.when("install", () => "install" as const),
    Match.when("connect", () => "connect" as const),
    Match.exhaustive,
  );

/**
 * The post-Connect client-only stops: they have no server `OnboardingStep`, so
 * the backend-derived current stop can never point at them. The layout reads
 * the active stop from the ROUTE for these two so the rail (and the Connect
 * breadcrumb, which only shows on the Connect stop) reflect where the customer
 * actually is once they leave Connect.
 *
 * @since 1.0.0
 */
export const TERMINAL_RAIL_STEPS: ReadonlyArray<OnboardingRailStepId> = ["verify", "done"];

/**
 * Pure: the terminal rail stop (`verify` / `done`) named by a path's last
 * segment, or `null` for any other path. Lets the layout override the
 * backend-derived current stop once the customer is past Connect.
 *
 * @since 1.0.0
 */
export const terminalRailStepForPath = (pathname: string): OnboardingRailStepId | null => {
  const segment = pathname.split("/").pop();
  return TERMINAL_RAIL_STEPS.find((id) => id === segment) ?? null;
};

/**
 * Pure: whether a path is the magic-link Welcome (`/…/onboarding/start/<token>`).
 * The Welcome is a standalone full-page `.onboard` frame with its own header,
 * stepper, and footer (CLA-877) — unlike the in-flow `.flow` step screens, it
 * must NOT render inside the shared onboarding shell chrome, so the layout uses
 * this to render it bare.
 *
 * @since 1.0.0
 */
export const isOnboardingWelcomePath = (pathname: string): boolean =>
  pathname.startsWith(`${ONBOARDING_BASE_PATH}/start/`);

/**
 * Completed/current/upcoming for a stop, by position relative to `current`.
 * Unknown ids sort as `-1` (before everything), so they read as `upcoming`.
 *
 * @since 1.0.0
 */
export const railStepStatus = (
  current: OnboardingRailStepId,
  step: OnboardingRailStepId,
): OnboardingStepStatus => {
  const order = ONBOARDING_RAIL_STEPS.map((s) => s.id);
  const currentIndex = order.indexOf(current);
  const stepIndex = order.indexOf(step);
  if (stepIndex < currentIndex) return "completed";
  if (stepIndex === currentIndex) return "current";
  return "upcoming";
};

/**
 * Loading-aware projection of `onboarding/state` for the resume resolver. Kept
 * free of effect-atom `Result` so it stays trivially testable; the route maps
 * its `Result<OnboardingState>` into one of these shapes.
 *
 * @since 1.0.0
 */
export type OnboardingStateView =
  | { readonly kind: "loading" }
  | { readonly kind: "ready"; readonly step: OnboardingStep; readonly intakeId: string }
  | { readonly kind: "error" };

/**
 * Loading-aware projection of the magic-link token's `resolveLink` result for
 * the resume resolver. Only the intake identity matters here (the full Welcome
 * context stays in the route), so a resolved link is `ready` with its
 * `intakeId`, and any dead / errored link is `unavailable` — the resolver can
 * no longer reconcile it against the signed-in state, so it falls back to the
 * existing resume (CLA-479).
 *
 * @since 1.0.0
 */
export type OnboardingLinkView =
  | { readonly kind: "loading" }
  | { readonly kind: "ready"; readonly intakeId: string }
  | { readonly kind: "unavailable" }
  // No magic-link token in play at all (e.g. a signed-in self-serve visitor who
  // navigated straight to a step rather than via a link). There is no intake to
  // reconcile, so a caller with existing state simply resumes (CLA-1033).
  | { readonly kind: "none" };

/**
 * Inputs the `start/$token` route feeds the resume resolver.
 *
 * @since 1.0.0
 */
export interface ResumeInput {
  readonly clerkLoaded: boolean;
  readonly signedIn: boolean;
  /**
   * Whether a Clerk `__session` cookie is present (see
   * `readClerkSessionCookieToken`). When absent, the visitor provably has no
   * session, so the Welcome must NOT block on clerk-js booting — the magic
   * link's first impression is an anonymous, token-gated page, and clerk-js
   * can hang entirely in restricted contexts (incognito third-party storage
   * clamps broke the dev-instance handshake on 2026-06-12, leaving the
   * customer on a spinner forever).
   */
  readonly sessionCookiePresent: boolean;
  readonly state: OnboardingStateView;
  /**
   * The magic-link token's resolved intake, projected from `resolveLink`. Lets
   * a signed-in caller with existing onboarding state reconcile the link they
   * followed against the intake they are mid-flow on: a FRESH link for a
   * DIFFERENT intake must route the new Welcome, never silently resume the old
   * one (CLA-479).
   */
  readonly link: OnboardingLinkView;
}

/**
 * Where the magic-link landing should send the customer. `loading` holds the
 * spinner until Clerk (and, when signed in, the state RPC) settle.
 *
 * @since 1.0.0
 */
export type ResumeTarget =
  | { readonly kind: "loading" }
  | { readonly kind: "redirect"; readonly path: string; readonly withToken: boolean };

/**
 * Resolve the resume destination for a magic-link landing — the core of
 * "returning via the link or after login resumes at the right step":
 *
 *  - Clerk still booting WITHOUT a session cookie → Welcome immediately (the
 *    visitor provably has no session; never block the anonymous first
 *    impression on clerk-js).
 *  - Clerk still booting WITH a session cookie → wait (might be a returning
 *    customer who should resume).
 *  - Not signed in → Account (carry the token so sign-up can bind it).
 *  - Signed in, state still loading → wait.
 *  - Signed in, state ready → reconcile against the magic-link token
 *    ({@link resolveSignedInResume}): the SAME intake resumes the derived rail
 *    stop; a DIFFERENT intake's fresh link routes its own Welcome so a second
 *    intake is never swallowed by the existing resume (CLA-479); a dead/errored
 *    link (which can no longer be reconciled) falls back to the resume.
 *  - Signed in, state errored → fall back to Account (carry the token), since
 *    an unresolved/Unauthorized state cannot place the customer.
 *
 * @since 1.0.0
 */
export const resolveResumeTarget = (input: ResumeInput): ResumeTarget => {
  if (!input.clerkLoaded) {
    return input.sessionCookiePresent
      ? { kind: "loading" }
      : { kind: "redirect", path: onboardingStepPath("account"), withToken: true };
  }
  if (!input.signedIn) {
    return { kind: "redirect", path: onboardingStepPath("account"), withToken: true };
  }
  return Match.value(input.state).pipe(
    Match.when({ kind: "loading" }, (): ResumeTarget => ({ kind: "loading" })),
    Match.when({ kind: "ready" }, (s): ResumeTarget => resolveSignedInResume(s, input.link)),
    Match.when({ kind: "error" }, (): ResumeTarget => ({
      kind: "redirect",
      path: onboardingStepPath("account"),
      withToken: true,
    })),
    Match.exhaustive,
  );
};

/**
 * Where a signed-in caller with READY onboarding state lands, once reconciled
 * against the magic-link token they followed (CLA-479). The token's intake is
 * the tiebreaker:
 *
 *  - link still resolving → wait (never resume before we know which intake the
 *    link points at — resuming first is exactly the hijack this fixes).
 *  - link ready, SAME intake → resume the derived rail stop (the CLA-449
 *    returning-customer path).
 *  - link ready, DIFFERENT intake → route the new intake's Welcome (carry the
 *    token) instead of silently resuming the old flow.
 *  - link unavailable (dead / already-used / errored — no intake to compare) →
 *    resume, preserving the returning-customer behaviour for a customer's own
 *    single-use link.
 *
 * @since 1.0.0
 */
export const resolveSignedInResume = (
  state: { readonly step: OnboardingStep; readonly intakeId: string },
  link: OnboardingLinkView,
): ResumeTarget => {
  const resume: ResumeTarget = {
    kind: "redirect",
    path: onboardingStepPath(railStepForBackendStep(state.step)),
    withToken: false,
  };
  return Match.value(link).pipe(
    Match.when({ kind: "loading" }, (): ResumeTarget => ({ kind: "loading" })),
    Match.when({ kind: "unavailable" }, (): ResumeTarget => resume),
    Match.when({ kind: "none" }, (): ResumeTarget => resume),
    Match.when(
      { kind: "ready" },
      (l): ResumeTarget =>
        l.intakeId === state.intakeId
          ? resume
          : { kind: "redirect", path: onboardingStepPath("account"), withToken: true },
    ),
    Match.exhaustive,
  );
};

/**
 * Completed/current/upcoming for a Connect sub-step, by position relative to
 * `current`.
 *
 * @since 1.0.0
 */
export const connectSubStepStatus = (
  current: OnboardingConnectSubStepId,
  step: OnboardingConnectSubStepId,
): OnboardingStepStatus => {
  const order = ONBOARDING_CONNECT_SUBSTEPS.map((s) => s.id);
  const currentIndex = order.indexOf(current);
  const stepIndex = order.indexOf(step);
  if (stepIndex < currentIndex) return "completed";
  if (stepIndex === currentIndex) return "current";
  return "upcoming";
};
